import { useRef } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Skeleton } from '@/components/atoms/skeleton';
import { TechnicianForm } from '@/components/organisms/technicians/TechnicianForm';
import {
  buildTechnicianValuesFromDetail,
  extractTechnicianExpertiseIds,
  extractTechnicianExpertiseLevels,
  extractTechnicianExpertiseExpiry,
  extractTechnicianServiceAreaZips,
  technicianServiceAreaIdByZip,
  type TechnicianFormDetail,
  type TechnicianFormValues,
} from '@/components/organisms/technicians/technician-form-utils';
import { saveTechnicianRelations } from '@/components/organisms/technicians/technician-form-save';
import { routeFieldsToParts } from '@/components/organisms/technicians/route-address';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useServiceTypes } from '@/hooks/scheduler/useServiceTypes';
import { useServiceAreas } from '@/hooks/service-areas/useServiceAreas';
import { apiGet, apiPatch, operationsClient } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { schedulerKeys } from '@/lib/queryKeys';

export const Route = createFileRoute('/_dashboard/dashboard/technicians/$id/edit')({
  component: EditTechnicianPage,
});

function EditTechnicianPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const serviceTypesQuery = useServiceTypes();
  const { data: serviceAreas = [] } = useServiceAreas();

  const technicianQuery = useApiQuery<TechnicianFormDetail>({
    queryKey: schedulerKeys.detail(id),
    queryFn: () =>
      apiGet<TechnicianFormDetail>(operationsClient, schedulerEndpoints.technician(id)),
    enabled: Boolean(id),
  });

  // The detail query refetches several times around mount (refetchOnMount +
  // StrictMode), so it can momentarily report no data after having loaded. Keep
  // the last-good detail so the form stays mounted across transient refetch/GC
  // (otherwise TechnicianForm remounts and its lazy useState init wipes unsaved
  // edits, e.g. an inline-created skill — NOT-#37 follow-up). Scope the snapshot
  // to the current technician id so navigating A→B never renders or saves B with
  // A's data.
  const lastGood = useRef<{ id: string; data: TechnicianFormDetail } | null>(null);
  if (technicianQuery.data) lastGood.current = { id, data: technicianQuery.data };
  const snapshot = lastGood.current?.id === id ? lastGood.current.data : null;
  const technician = technicianQuery.data ?? snapshot;

  const updateMutation = useApiMutation<TechnicianFormDetail, TechnicianFormValues>({
    mutationFn: async (values) => {
      // Diff relations against the same resolved snapshot we render from — never
      // raw query.data, which can be undefined mid-refetch and make the save
      // re-POST every selected relation as new (duplicates / 409).
      const currentTechnician = technician ?? undefined;
      const updated = await apiPatch<TechnicianFormDetail>(
        operationsClient,
        schedulerEndpoints.technician(id),
        {
          name: values.name,
          email: values.email,
          phone: values.phone || null,
          bio: values.bio.trim() || null,
          capacityPerDay: values.capacityPerDay,
          capacityPerWeek: values.capacityPerWeek,
          maxHoursPerDay: values.maxHoursPerDay,
          maxConsecutiveHours: values.maxConsecutiveHours,
          minBreakMinutes: values.minBreakMinutes,
          maxDriveTimeMinutesPerDay: values.maxDriveTimeMinutesPerDay,
          timezone: values.timezone,
          isActive: values.isActive,
          startAddressParts: routeFieldsToParts(values.startAddress),
          endAddressParts: routeFieldsToParts(values.endAddress),
        },
      );
      await saveTechnicianRelations(id, values, {
        initialExpertiseIds: extractTechnicianExpertiseIds(currentTechnician),
        initialExpertiseLevels: extractTechnicianExpertiseLevels(currentTechnician),
        initialExpertiseExpiry: extractTechnicianExpertiseExpiry(currentTechnician),
        initialServiceAreaZips: extractTechnicianServiceAreaZips(currentTechnician),
        serviceAreaIdByZip: technicianServiceAreaIdByZip(currentTechnician),
      });
      return updated;
    },
    onSuccess: (technician) => {
      void queryClient.invalidateQueries({ queryKey: schedulerKeys.technicians() });
      void queryClient.invalidateQueries({ queryKey: schedulerKeys.detail(id) });
      toast.success('Technician updated');
      void navigate({
        to: '/dashboard/technicians/$id',
        params: { id: technician.id },
      });
    },
  });

  function goBack() {
    void navigate({
      to: '/dashboard/technicians/$id',
      params: { id },
    });
  }

  if (technicianQuery.isLoading && !technician) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!technician) {
    return <div className="p-4 text-sm text-muted-foreground">Technician not found.</div>;
  }

  return (
    <TechnicianForm
      mode="edit"
      initialValues={buildTechnicianValuesFromDetail(technician)}
      submitting={updateMutation.isPending}
      onBack={goBack}
      onSubmit={(values) => updateMutation.mutate(values)}
      technician={technician}
      technicianId={id}
      photoUrl={technician.photoUrl}
      serviceTypes={serviceTypesQuery.data ?? []}
      serviceAreas={serviceAreas}
      loadingOptions={serviceTypesQuery.isLoading}
    />
  );
}
